import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchTransactionImageUrl, useAddInvestment, useInvestments } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { Investment } from '../../types';
import { apiDate, date, money, percent } from '../../utils/format';
import PendingDocumentPicker from './PendingDocumentPicker';
import TransactionImageLink from './TransactionImageLink';
import QuickShareButton from '../common/QuickShareButton';
import { usePartyContact } from '../common/usePartyContact';
import { transactionMessage } from '../../utils/quickShare';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import TransactionAccountField from '../common/TransactionAccountField';
import { useTransactionAccountField } from '../../utils/transactionAccountField';
import { useTranslation } from 'react-i18next';
import AutoText from '../common/AutoText';

export default function InvestmentsTab({ investorId }: { investorId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useInvestments(investorId);
  const contact = usePartyContact('Investor', investorId, 'PercentageBased');
  const { canWrite, hasRole } = useAuth();
  // Investors get a single statement PDF from the Ledger tab instead of per-row receipts.
  const showReceipts = !hasRole('Investor');
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addInvestment = useAddInvestment(investorId);
  const accountField = useTransactionAccountField('Investor', investorId);
  // Watched so the account/wallet field can disappear the moment Cash is chosen.
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addInvestment.mutateAsync({
        amount: values.amount as number,
        profitRate: values.profitRate as number,
        investmentDate: apiDate(values.investmentDate as dayjs.Dayjs)!,
        notes: values.notes as string | undefined,
        paymentMethod: values.paymentMethod as string | undefined,
        transactionImage,
      });
      // Persists an edited account/wallet number onto the profile, matching the profit tab.
      await accountField.commitIfChanged();
      message.success('Investment added.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  // Summed from the tranches on screen rather than fetched separately, so the cards and the table
  // can never disagree. The fixed book gets the same treatment from its own account totals.
  const rows = data ?? [];
  const totals = rows.reduce(
    (sum, row) => ({
      amount: sum.amount + row.amount,
      withdrawn: sum.withdrawn + row.withdrawnAmount,
      remaining: sum.remaining + row.remainingAmount,
    }),
    { amount: 0, withdrawn: 0, remaining: 0 },
  );

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Add investment
          </Button>
        </Space>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('finance.totalInvestment')}
              value={money(totals.amount)}
              prefix="PKR"
              valueStyle={{ fontSize: 20 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Capital paid in
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('finance.totalWithdrawn')}
              value={money(totals.withdrawn)}
              prefix="PKR"
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('finance.totalInvestmentRemaining')}
              value={money(totals.remaining)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#FE9F43' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Still placed
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Table<Investment>
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        summary={(rows) => {
          const principal = rows.reduce((sum, r) => sum + r.remainingAmount, 0);
          return (
            <Table.Summary.Row>
              {/* #, Date, Amount, Payment method, Withdrawn — the total sits under Remaining. */}
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>{t('finance.totalInvestmentRemaining')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <strong>{money(principal)}</strong>
              </Table.Summary.Cell>
              {/* Rate, Status, Notes, Transaction proof — plus Receipt when it is shown. */}
              <Table.Summary.Cell index={6} colSpan={showReceipts ? 5 : 4} />
            </Table.Summary.Row>
          );
        }}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: t('common.date'), dataIndex: 'investmentDate', render: date },
          { title: t('finance.totalAmount'), dataIndex: 'amount', align: 'right', render: money },
          { title: t('finance.paymentMethod'), dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
          { title: t('finance.withdrawn'), dataIndex: 'withdrawnAmount', align: 'right', render: money },
          { title: t('finance.totalRemaining'), dataIndex: 'remainingAmount', align: 'right', render: money },
          { title: t('table.rate'), dataIndex: 'profitRate', align: 'right', render: percent },
          {
            title: t('common.status'),
            dataIndex: 'isActive',
            render: (active: boolean) => <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Closed'}</Tag>,
          },
          { title: t('common.notes'), dataIndex: 'notes', render: (v: string | null) => <AutoText value={v} /> },
          {
            title: t('finance.transactionProof'),
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — tranche #${row.id}`}
                fetchUrl={() => fetchTransactionImageUrl(investorId, row.id)}
              />
            ),
          },
          ...(showReceipts
            ? [
                {
                  title: t('finance.receipt'),
                  render: (_: unknown, row: Investment) => (
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        downloadFile(`/receipts/investment/${row.id}`, `investment_receipt_${row.id}.pdf`).catch((err) =>
                          message.error(errorMessage(err)),
                        )
                      }
                    >
                      PDF
                    </Button>
                  ),
                },
              ]
            : []),
          ...(contact.ready
            ? [
                {
                  title: t('common.share'),
                  render: (_: unknown, row: Investment) => (
                    <QuickShareButton
                      phone={contact.phone}
                      recipientName={contact.name}
                      message={transactionMessage(contact, 'New investment', row.amount, row.investmentDate, { position: 'capital' })}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal maskClosable={false}
        title="Add investment tranche"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addInvestment.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} initialValues={{ investmentDate: dayjs() }}>
          <Form.Item
            name="amount"
            label={t('finance.amountPkr')}
            rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="profitRate"
            label={t('finance.monthlyProfitRate')}
            rules={[{ required: true }, { type: 'number', min: 0.01, max: 99.99, message: 'Rate must be 0.01–99.99.' }]}
          >
            <InputNumber min={0.01} max={99.99} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="investmentDate" label={t('finance.investmentDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="paymentMethod" label={t('finance.paymentMethod')}>
            <Select placeholder="How the money came in" allowClear options={paymentMethodOptions} />
          </Form.Item>
          <TransactionAccountField field={accountField} paymentMethod={paymentMethod} />

          <Form.Item name="notes" label={t('common.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="transaction-image"
            label={t('finance.transactionProofOptional')}
            hint="Bank slip, JazzCash/EasyPaisa screenshot or cash voucher · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
